// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import './MissingValue.css';

/**
 * A figure the design calls for that no contract serves yet.
 *
 * A screen keeps its designed shape under missing data rather than collapsing
 * the section: the tile, row or cell stays where the design put it, and the
 * value position carries an em dash naming what is missing. `reason` is the
 * contract-level gap ("no endpoint publishes plan geometry"), never a generic
 * "no data" — a reader has to be able to tell **a real zero** from **nobody
 * publishes this**, and the two must not look alike.
 *
 * The reason reaches assistive tech via `aria-label`, so the dash is never
 * announced bare.
 */
export function MissingValue({ reason }: { reason: string }) {
  return (
    <span className="ui-missing" title={reason} aria-label={`Not available — ${reason}`}>
      —
    </span>
  );
}

/** The same gap stated in prose, for a caption slot under a value. */
export function MissingNote({ children }: { children: React.ReactNode }) {
  return <p className="ui-missing-note">{children}</p>;
}

/**
 * A region whose contents cannot be built at all, sat *inside* the container the
 * design asks for rather than replacing it. Used where there is no row or shape
 * to draw, but the container — and the fact that something belongs in it — is
 * still part of the design.
 */
export function MissingRegion({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="ui-missing-region" role="note">
      <span className="ui-missing-region-title">{title}</span>
      <p className="ui-missing-note">{detail}</p>
    </div>
  );
}
